import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../supabase/client';
import { validateEmailFormat, normalizeEmail } from '../utils/emailValidation';
import { isPasswordValid, getPasswordError } from '../utils/passwordValidation';
import { enhancedSignUp, enhancedSignIn, ExistingAccount } from '../utils/accountLinking';

// Production-ready AuthContext without debug logging

type AuthContextType = {
  session: Session | null;
  user: User | null; // Derived from session
  signIn: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  signUp: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  signInWithGoogle: () => Promise<{ success: boolean; message?: string }>;
  signInWithOTP: (email: string) => Promise<{ success: boolean; message?: string }>;
  verifyOTP: (email: string, token: string) => Promise<{ success: boolean; message?: string }>;
  resetPassword: (email: string) => Promise<{ success: boolean; message?: string }>;
  changePassword: (newPassword: string) => Promise<{ success: boolean; message?: string }>;
  signOut: () => Promise<void>;
  loading: boolean;
  error: string | null;
  clearMessages: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  
  const [session, setSession] = useState<Session | null>(null);
  // Remove separate user state - derive from session
  const user = session?.user ?? null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Remove successMessage - move to AuthModal component

  // Initialize auth state - run once only
  useEffect(() => {
    // console.log('🚀 AuthProvider useEffect running - initializing auth state');
    
    // Get initial session
    supabase.auth.getSession().then(({ data, error }) => {
      if (error) {
        // console.error('Error getting initial session:', error);
        setError(`Authentication initialization failed: ${error.message}`);
      } else {
        setSession(data.session);
        // User is now derived from session
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // console.log('🔄 Auth state changed:', event, session?.user?.email || 'no user');
      
      // Preserve layout state during auth transitions
      if (event === 'SIGNED_IN') {
        const currentScrollY = window.scrollY;
        const layoutState = {
          scrollY: currentScrollY,
          timestamp: Date.now(),
          userAgent: navigator.userAgent
        };
        
        // Store layout state to prevent 'scroll up' appearance
        sessionStorage.setItem('zoningpal_layout_state', JSON.stringify(layoutState));

      }
      
      setSession(session);
      // User is now derived from session
      
      if (event === 'SIGNED_IN') {
        // Clear errors on successful sign in
        setError(null);
      } else if (event === 'SIGNED_OUT') {
        // Clear errors and layout state on sign out
        setError(null);
        sessionStorage.removeItem('zoningpal_layout_state');
      }
    });

    // Periodically validate session to handle multi-device sign-outs
    const sessionCheckInterval = setInterval(async () => {
      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.log('⚠️ Session validation error:', error);
          // Clear invalid session
          setSession(null);
        } else if (!currentSession && session) {
          console.log('🔄 Session invalidated on server, clearing local session...');
          // Server session is null but we have a local session - clear it
          setSession(null);
        }
      } catch (error) {
        console.log('⚠️ Session validation check failed:', error);
      }
    }, 60000); // Check every minute

    return () => {
      // console.log('🧹 AuthProvider useEffect cleanup');
      subscription.unsubscribe();
      clearInterval(sessionCheckInterval);
    };
  }, []); // Empty dependency array - run once only

  // Stable function references - never recreate
  const clearMessages = useCallback(() => {
    setError(null);
    // successMessage removed - handled in AuthModal
  }, []);

  const signUp = useCallback(async (email: string, password: string): Promise<{ 
    success: boolean; 
    message?: string;
    action?: string;
    existingAccount?: ExistingAccount;
  }> => {
    try {
      setLoading(true);
      setError(null);
      
      // Enhanced validation for sign up
      if (!email || !password) {
        const message = 'Please enter both email and password';
        setError(message);
        return { success: false, message };
      }

      // Comprehensive email validation
      const emailValidation = validateEmailFormat(email);
      if (!emailValidation.isValid) {
        setError(emailValidation.error || 'Please enter a valid email address');
        return { success: false, message: emailValidation.error || 'Please enter a valid email address' };
      }

      // Comprehensive password validation
      if (!isPasswordValid(password)) {
        const passwordError = getPasswordError(password);
        setError(passwordError || 'Password does not meet security requirements');
        return { success: false, message: passwordError || 'Password does not meet security requirements' };
      }
      
      console.log('🚀 Starting enhanced signup for:', email);
      console.log('🕐 Signup attempt timestamp:', new Date().toISOString());
      
      // Use enhanced signup with duplicate prevention and friendly messages
      const result = await enhancedSignUp(email, password);
      
      console.log('📊 Enhanced signup result:', { 
        hasData: !!result.data, 
        hasError: !!result.error,
        action: result.action,
        hasExistingAccount: !!result.existingAccount
      });
      
      if (result.error) {
        console.log('❌ Enhanced signup error:', result.error.message);
        setError(result.error.message);
        return { 
          success: false, 
          message: result.error.message,
          action: result.action,
          existingAccount: result.existingAccount 
        };
      }
      
      // Enhanced success detection - check for actual new account creation
      if (result.data?.user) {
        console.log('📊 Detailed enhanced signup response:', {
          userExists: !!result.data.user,
          hasSession: !!result.data.session,
          userCreatedAt: result.data.user.created_at,
          userLastSignIn: result.data.user.last_sign_in_at,
          userEmailConfirmed: result.data.user.email_confirmed_at
        });
        
        console.log('✅ New account created successfully with enhanced flow for:', email);
        
        // Check if email confirmation is required (most common case)
        const needsEmailConfirmation = !result.data.session;
        const message = needsEmailConfirmation
          ? 'Account created! Please check your email and click the confirmation link to complete your registration.'
          : 'Welcome to ZoningPal! Your account has been created and you are now signed in.';
          
        return { success: true, message };
      } else {
        console.log('⚠️ Enhanced signup completed but no user returned');
        const message = 'Account creation failed. Please try again.';
        setError(message);
        return { success: false, message };
      }
      
    } catch (error: any) {
      console.error('💥 Unexpected enhanced signup error:', error);
      const message = process.env.NODE_ENV === 'development'
        ? `An unexpected error occurred: ${error.message}`
        : 'An unexpected error occurred. Please try again.';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<{ 
    success: boolean; 
    message?: string;
    action?: string;
    existingAccount?: ExistingAccount;
  }> => {
    try {
      setLoading(true);
      setError(null);
      
      if (!email || !password) {
        const message = 'Please enter both email and password';
        setError(message);
        return { success: false, message };
      }
      
      console.log('🔑 Starting enhanced signin for:', email);
      console.log('🕐 Signin attempt timestamp:', new Date().toISOString());
      
      // Use enhanced signin with better error handling
      const result = await enhancedSignIn(email, password);
      
      console.log('📊 Enhanced signin result:', { 
        hasData: !!result.data, 
        hasError: !!result.error,
        action: result.action,
        hasExistingAccount: !!result.existingAccount
      });

      if (result.error) {
        console.log('❌ Enhanced signin error:', result.error.message);
        setError(result.error.message);
        return { 
          success: false, 
          message: result.error.message,
          action: result.action,
          existingAccount: result.existingAccount 
        };
      }
      
      if (result.data?.session && result.data?.user) {
        const isConfirmed = result.data.user.email_confirmed_at;
        const message = isConfirmed 
          ? `Welcome back, ${result.data.user.email}!`
          : 'Signed in! Please confirm your email to access all features.';
        
        console.log('✅ Enhanced signin successful for:', email);
        return { success: true, message };
      } else {
        const message = 'Sign in completed but no session created. Please try again.';
        setError(message);
        return { success: false, message };
      }
      
    } catch (error: any) {
      console.error('💥 Unexpected enhanced signin error:', error);
      const message = 'An unexpected error occurred. Please try again.';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  const signInWithGoogle = useCallback(async (): Promise<{ success: boolean; message?: string }> => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: authError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: process.env.NODE_ENV === 'production'
            ? `${window.location.origin}/auth/callback`
            : 'http://localhost:3000/auth/callback',
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (authError) {
        console.log('❌ Google sign-in error:', authError);
        let friendlyMessage = 'Google sign-in failed. Please try again.';
        
        if (authError.message.includes('popup') || authError.message.includes('blocked')) {
          friendlyMessage = 'Pop-up was blocked. Please allow pop-ups for this site and try again.';
        } else if (authError.message.includes('cancelled') || authError.message.includes('denied')) {
          friendlyMessage = 'Google sign-in was cancelled. Please try again if you want to continue.';
        }
        
        setError(friendlyMessage);
        return { success: false, message: friendlyMessage };
      }

      // OAuth redirect will handle the rest
      return { success: true, message: 'Redirecting to Google...' };
      
    } catch (error: any) {
      console.error('💥 Unexpected Google sign-in error:', error);
      const message = 'An unexpected error occurred with Google sign-in. Please try again.';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  const signInWithOTP = useCallback(async (email: string): Promise<{ success: boolean; message?: string }> => {
    try {
      setLoading(true);
      setError(null);

      // Normalize email for consistent authentication
      const normalizedEmail = normalizeEmail(email);

      const { data, error: authError } = await supabase.auth.signInWithOtp({
        email: normalizedEmail,
        options: {
          shouldCreateUser: true, // Allow creating user if they don't exist
          emailRedirectTo: undefined // Don't use email redirect, we'll verify with OTP
        }
      });

      if (authError) {
        console.log('❌ OTP sign-in error:', authError);
        let friendlyMessage = 'Failed to send verification code. Please try again.';
        
        if (authError.message.includes('rate limit') || authError.message.includes('Too many')) {
          friendlyMessage = 'Too many requests. Please wait a few minutes before trying again.';
        } else if (authError.message.includes('Invalid email')) {
          friendlyMessage = 'Please enter a valid email address.';
        }
        
        setError(friendlyMessage);
        return { success: false, message: friendlyMessage };
      }

      console.log('✅ OTP sent successfully to:', normalizedEmail);
      return { success: true, message: `We've sent a 6-digit code to ${normalizedEmail}` };
      
    } catch (error: any) {
      console.error('💥 Unexpected OTP error:', error);
      const message = 'An unexpected error occurred. Please try again.';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOTP = useCallback(async (email: string, token: string): Promise<{ success: boolean; message?: string }> => {
    try {
      setLoading(true);
      setError(null);

      // Normalize email for consistent authentication
      const normalizedEmail = normalizeEmail(email);

      const { data, error: authError } = await supabase.auth.verifyOtp({
        email: normalizedEmail,
        token: token.replace(/\s/g, ''), // Remove any spaces
        type: 'email'
      });

      if (authError) {
        console.log('❌ OTP verification error:', authError);
        let friendlyMessage = 'Invalid verification code. Please try again.';
        
        if (authError.message.includes('expired')) {
          friendlyMessage = 'Verification code has expired. Please request a new one.';
        } else if (authError.message.includes('invalid') || authError.message.includes('wrong')) {
          friendlyMessage = 'Invalid verification code. Please check and try again.';
        } else if (authError.message.includes('rate limit')) {
          friendlyMessage = 'Too many attempts. Please wait a few minutes before trying again.';
        }
        
        setError(friendlyMessage);
        return { success: false, message: friendlyMessage };
      }

      if (data.session && data.user) {
        console.log('✅ OTP verification successful for:', normalizedEmail);
        const isNewUser = data.user.created_at === data.user.last_sign_in_at;
        const message = isNewUser 
          ? 'Welcome to ZoningPal! Your account has been created.'
          : `Welcome back, ${data.user.email}!`;
        
        return { success: true, message };
      } else {
        const message = 'Verification completed but no session created. Please try again.';
        setError(message);
        return { success: false, message };
      }
      
    } catch (error: any) {
      console.error('💥 Unexpected OTP verification error:', error);
      const message = 'An unexpected error occurred. Please try again.';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async (email: string): Promise<{ success: boolean; message?: string }> => {
    try {
      setLoading(true);
      setError(null);

      // Email format validation
      const emailValidation = validateEmailFormat(email);
      if (!emailValidation.isValid) {
        const message = emailValidation.error || 'Please enter a valid email address';
        setError(message);
        return { success: false, message };
      }

      // Normalize email for consistent authentication
      const normalizedEmail = normalizeEmail(email);

      const { error: authError } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo: process.env.NODE_ENV === 'development'
          ? 'http://localhost:3000/reset-password'
          : `${window.location.origin}/reset-password`
      });

      if (authError) {
        console.log('❌ Password reset error:', authError);
        console.log('❌ Full error details:', JSON.stringify(authError, null, 2));
        
        let friendlyMessage = 'Failed to send password reset email. Please try again.';
        
        if (authError.message.includes('rate limit') || authError.message.includes('Too many') || authError.status === 429) {
          friendlyMessage = 'Too many password reset attempts. Please wait 10-15 minutes before trying again.';
        } else if (authError.message.includes('Invalid email')) {
          friendlyMessage = 'Please enter a valid email address.';
        } else if (authError.message.includes('not found') || authError.message.includes('User not found')) {
          // For security, don't reveal if email exists or not
          friendlyMessage = 'If an account with this email exists, you will receive password reset instructions.';
        } else if (authError.message.includes('SMTP') || authError.message.includes('Email')) {
          friendlyMessage = 'Email service temporarily unavailable. Please try again in a few minutes or contact support.';
        }
        
        // In development, show more details
        if (process.env.NODE_ENV === 'development') {
          friendlyMessage += ` (Debug: ${authError.message})`;
        }
        
        setError(friendlyMessage);
        return { success: false, message: friendlyMessage };
      }

      console.log('✅ Password reset email sent to:', normalizedEmail);
      const message = 'Password reset instructions have been sent to your email.';
      return { success: true, message };
      
    } catch (error: any) {
      console.error('💥 Unexpected password reset error:', error);
      const message = 'An unexpected error occurred. Please try again.';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  const changePassword = useCallback(async (newPassword: string): Promise<{ success: boolean; message?: string }> => {
    try {
      setLoading(true);
      setError(null);

      // Password validation
      if (!newPassword) {
        const message = 'Please enter a new password';
        setError(message);
        return { success: false, message };
      }

      const passwordError = getPasswordError(newPassword);
      if (passwordError) {
        setError(passwordError);
        return { success: false, message: passwordError };
      }

      const { error: authError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (authError) {
        console.log('❌ Password change error:', authError);
        let friendlyMessage = 'Failed to change password. Please try again.';
        
        if (authError.message.includes('session')) {
          friendlyMessage = 'Your session has expired. Please sign out and sign back in.';
        } else if (authError.message.includes('weak password')) {
          friendlyMessage = 'Please choose a stronger password with a mix of letters, numbers, and symbols.';
        } else if (authError.message.includes('rate limit')) {
          friendlyMessage = 'Too many attempts. Please wait a few minutes before trying again.';
        }
        
        setError(friendlyMessage);
        return { success: false, message: friendlyMessage };
      }

      console.log('✅ Password changed successfully');
      const message = 'Your password has been changed successfully.';
      return { success: true, message };
      
    } catch (error: any) {
      console.error('💥 Unexpected password change error:', error);
      const message = 'An unexpected error occurred. Please try again.';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Attempting to sign out...');
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.log('❌ Server-side sign out error:', error);
        
        // If the error is about an invalid session, clear local session anyway
        if (error.message.includes('invalid') || 
            error.message.includes('expired') || 
            error.message.includes('not found') ||
            error.message.includes('session')) {
          console.log('🔄 Session already invalid on server, clearing local session...');
          // Clear local session manually since server-side is already cleared
          setSession(null);
        } else {
          // For other errors, show error message
          setError('Failed to sign out. Please try again.');
        }
      } else {
        console.log('✅ Sign out successful');
        // Session will be cleared automatically by onAuthStateChange
      }
      
    } catch (error: any) {
      console.error('💥 Unexpected sign out error:', error);
      
      // Clear local session anyway since we can't verify server state
      console.log('🔄 Clearing local session due to error...');
      setSession(null);
      
      setError('An error occurred while signing out.');
    } finally {
      setLoading(false);
    }
  }, []);

  // CRITICAL: Memoize context value so consumers only update when values actually change
  const value = useMemo(() => {
    return {
      session,
      user,
      signIn,
      signUp,
      signInWithGoogle,
      signInWithOTP,
      verifyOTP,
      resetPassword,
      changePassword,
      signOut,
      loading,
      error,
      clearMessages
    };
  }, [session, loading, error, signIn, signUp, signInWithGoogle, signInWithOTP, verifyOTP, resetPassword, changePassword, signOut, clearMessages]); // Remove 'user' - it's derived from session

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
